import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { studentApi } from '../services/api';
import { emitCreditsChanged } from '../utils/credits';
import { BusyRange, overlapsBusy, formatBusyRanges } from '../utils/appointmentAvailability';

interface Props {
  open: boolean;
  onClose: () => void;
  role: 'student' | 'teacher' | 'parent';
}

type Step = 'randevu-secimi' | 'kutuphane-slot-secimi' | 'onay' | 'ogretmen-secimi' | 'ogretmen-detay' | 'ogretmen-onay';

interface LibrarySlot {
  id: string;
  institution_name: string;
  start_at: string;
  end_at: string;
  capacity: number;
  reserved_count: number;
  credit_cost: number;
  assigned_teacher_name?: string | null;
  my_status?: 'booked' | null;
}

interface TeacherOpt {
  id: string;
  uuid: string;
  firstName: string;
  lastName: string;
  subjects: { id: number; name: string }[];
}

const CloseButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button onClick={onClick} className="absolute top-4 right-4 xl:top-5 xl:right-5 w-[36px] h-[36px] xl:w-[42px] xl:h-[42px] bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors shadow-sm z-10">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
  </button>
);

const KalanKrediler: React.FC<{ balance: number | null }> = ({ balance }) => (
  <div className="bg-[#3973bf] rounded-[10px] px-3 py-1.5 flex items-center gap-2">
    <img src="/digipo-coin.svg" alt="" className="w-[20px] h-[20px] object-contain" />
    <span className="text-[18px] xl:text-[20px] font-bold text-white">{balance ?? '—'}</span>
    <span className="text-[10px] xl:text-[12px] font-bold text-white leading-tight">Kalan<br/>Krediler</span>
  </div>
);

const RandevuOlusturModal: React.FC<Props> = ({ open, onClose, role }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('randevu-secimi');
  const [credit, setCredit] = useState<number | null>(null);
  const [slots, setSlots] = useState<LibrarySlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [confirmedSlot, setConfirmedSlot] = useState<LibrarySlot | null>(null);

  // Öğretmen randevusu akışı
  const [teachers, setTeachers] = useState<TeacherOpt[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [teachersError, setTeachersError] = useState<string | null>(null);
  const [selTeacher, setSelTeacher] = useState<TeacherOpt | null>(null);
  const [subjectId, setSubjectId] = useState<number | null>(null);
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('09:30');
  const [format, setFormat] = useState<'face_to_face' | 'online' | 'phone'>('face_to_face');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // B7: teacher's already-booked ranges for the picked date.
  const [busy, setBusy] = useState<BusyRange[]>([]);
  const [busyLoading, setBusyLoading] = useState(false);
  const [busyError, setBusyError] = useState(false);
  const conflict = useMemo(() => overlapsBusy(startTime, endTime, busy), [startTime, endTime, busy]);

  useEffect(() => {
    if (!open) return;
    setStep('randevu-secimi');
    setBookingError(null);
    setConfirmedSlot(null);
    // Öğretmen akışını sıfırla
    setSelTeacher(null); setSubjectId(null); setDate('');
    setStartTime('09:00'); setEndTime('09:30'); setFormat('face_to_face');
    setNotes(''); setCreateError(null); setTeachersError(null); setBusy([]);
    if (role === 'student') {
      studentApi.credits().then((r) => setCredit(r.data?.balance ?? null)).catch(() => setCredit(null));
    }
  }, [open, role]);

  // B20: while the student is on the slot-picking step, poll availability every
  // 15s (silent, paused when the tab is hidden) so seats another student
  // grabs/frees update in near-real-time. Stops on leaving the step / closing.
  useEffect(() => {
    if (!open || step !== 'kutuphane-slot-secimi') return;
    const POLL_MS = 15_000;
    const timer = setInterval(() => { if (!document.hidden) loadLibrarySlots(true); }, POLL_MS);
    return () => clearInterval(timer);
  }, [open, step]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTeachers = () => {
    setStep('ogretmen-secimi');
    setTeachersLoading(true);
    setTeachersError(null);
    studentApi.appointmentTeachers()
      .then((r) => setTeachers(r.data ?? []))
      .catch((e: any) => setTeachersError(e?.response?.data?.error ?? 'Öğretmen listesi yüklenemedi'))
      .finally(() => setTeachersLoading(false));
  };

  const pickTeacher = (t: TeacherOpt) => {
    setSelTeacher(t);
    setSubjectId(t.subjects[0]?.id ?? null);
    setCreateError(null);
    setBusy([]);
    setStep('ogretmen-detay');
  };

  // B7: fetch the teacher's busy ranges whenever the teacher + date are chosen.
  useEffect(() => {
    if (!selTeacher || !date) { setBusy([]); setBusyError(false); return; }
    let cancelled = false;
    setBusyLoading(true);
    setBusyError(false);
    studentApi.teacherAvailability(selTeacher.id, date)
      .then((r) => { if (!cancelled) { setBusy(r.data?.busy ?? []); setBusyError(false); } })
      .catch(() => { if (!cancelled) { setBusy([]); setBusyError(true); } })
      .finally(() => { if (!cancelled) setBusyLoading(false); });
    return () => { cancelled = true; };
  }, [selTeacher, date]);

  const submitTeacherAppointment = async () => {
    if (!selTeacher || !date) { setCreateError('Lütfen öğretmen ve tarih seçin.'); return; }
    setSubmitting(true);
    setCreateError(null);
    try {
      await studentApi.createAppointment({
        teacherUserId: selTeacher.id,
        subjectId: subjectId ?? undefined,
        appointmentDate: `${date}T${startTime}:00`,
        startTime,
        endTime,
        format,
        notes: notes || undefined,
      });
      setStep('ogretmen-onay');
    } catch (e: any) {
      setCreateError(e?.response?.data?.error ?? e.message ?? 'Randevu oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  // silent=true → background poll: don't flash the "Yükleniyor…" spinner over
  // the already-rendered grid.
  const loadLibrarySlots = (silent = false) => {
    if (!silent) setLoadingSlots(true);
    studentApi.librarySlots()
      .then((r) => {
        setSlots(r.data?.slots ?? []);
        if (typeof r.data?.balance === 'number') {
          setCredit(r.data.balance);
          emitCreditsChanged(r.data.balance); // keep the TopBar coin in sync
        }
      })
      .catch(() => { if (!silent) setSlots([]); })
      .finally(() => { if (!silent) setLoadingSlots(false); });
  };

  const handleClose = () => {
    setStep('randevu-secimi');
    setConfirmedSlot(null);
    setBookingError(null);
    onClose();
  };

  const goToRandevular = () => {
    handleClose();
    navigate(`/${role === 'teacher' ? 'teacher' : role === 'parent' ? 'parent' : 'student'}/randevularim`);
  };

  const groupedByDay = useMemo(() => {
    const m = new Map<string, LibrarySlot[]>();
    for (const s of slots) {
      const day = new Date(s.start_at).toLocaleDateString('tr-TR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
      if (!m.has(day)) m.set(day, []);
      m.get(day)!.push(s);
    }
    return Array.from(m.entries());
  }, [slots]);

  const bookSlot = async (slot: LibrarySlot) => {
    setBookingId(slot.id);
    setBookingError(null);
    try {
      await studentApi.bookLibrarySlot(slot.id);
      setConfirmedSlot(slot);
      setStep('onay');
      // Slot grid'i + krediyi birlikte tazele: rezervasyon sonrası my_status
      // 'booked', reserved_count ve bakiye güncel kalsın (librarySlots yanıtı
      // balance de döndürüyor). Böylece grid'e geri dönülürse bayat görünmez.
      loadLibrarySlots();
    } catch (e: any) {
      setBookingError(e?.response?.data?.error ?? 'Rezervasyon yapılamadı');
    } finally {
      setBookingId(null);
    }
  };

  if (!open) return null;

  const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={handleClose} />

      {/* Wide shell for the library slot grid; narrower for the teacher
          detail/success steps whose content maxes at ~640px. */}
      <div className={`relative bg-[#f6f6f6] rounded-[20px] w-full ${
        step === 'ogretmen-detay' || step === 'ogretmen-onay' ? 'max-w-[720px]' : 'max-w-[1100px]'
      } max-h-[85vh] overflow-y-auto p-5 xl:p-7`}>
        <CloseButton onClick={handleClose} />

        {/* STEP 1: Randevu tipi seçimi — 3 kart: Kütüphane / Öğretmen / Danışman */}
        {step === 'randevu-secimi' && (
          <div>
            <div className="flex items-center justify-between mb-6 pr-12">
              <h2 className="text-[22px] xl:text-[28px] font-bold text-[#3973bf]">Randevu Seçimi</h2>
              {role === 'student' && <KalanKrediler balance={credit} />}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 xl:gap-4">
              {/* Kütüphane randevusu — gerçek slot sistemi */}
              <button
                onClick={() => { setStep('kutuphane-slot-secimi'); loadLibrarySlots(); }}
                className="bg-[#fafafa] rounded-[16px] h-[90px] xl:h-[105px] px-5 flex items-center justify-between border border-[#3973bf] hover:bg-blue-50 transition-colors text-left"
              >
                <div>
                  <span className="text-[14px] xl:text-[17px] font-bold text-[#2b2b2b] block">Kütüphane Randevusu</span>
                  <span className="text-[10px] xl:text-[12px] text-gray-500 flex items-center gap-1 mt-1">
                    <img src="/digipo-coin.svg" alt="" className="w-3 h-3" />
                    Kredi gerekir
                  </span>
                </div>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>

              {/* Öğretmen randevusu — modal içinde öğretmen seç + tarih/saat gir */}
              <button
                onClick={loadTeachers}
                className="bg-[#fafafa] rounded-[16px] h-[90px] xl:h-[105px] px-5 flex items-center justify-between border border-[#3973bf] hover:bg-blue-50 transition-colors text-left"
              >
                <div>
                  <span className="text-[14px] xl:text-[17px] font-bold text-[#2b2b2b] block">Öğretmen Randevusu</span>
                  <span className="text-[10px] xl:text-[12px] text-gray-500 block mt-1">Bir öğretmenle birebir görüşme</span>
                </div>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>

              {/* Danışman randevusu — henüz aktif değil */}
              <div
                aria-disabled
                className="bg-[#f2f2f2] rounded-[16px] h-[90px] xl:h-[105px] px-5 flex items-center justify-between border border-gray-200 opacity-60 cursor-not-allowed text-left"
              >
                <div>
                  <span className="text-[14px] xl:text-[17px] font-bold text-[#2b2b2b] block">Danışman Randevusu</span>
                  <span className="text-[10px] xl:text-[12px] text-gray-500 block mt-1">Kurum rehber danışmanlarıyla</span>
                </div>
                <span className="text-[10px] xl:text-[12px] font-semibold text-gray-400">Yakında</span>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Kütüphane slot seçimi — kurumun açtığı gerçek müsait saatler */}
        {step === 'kutuphane-slot-secimi' && (
          <div>
            <div className="flex items-center justify-between mb-5 pr-12">
              <div>
                <button onClick={() => setStep('randevu-secimi')} className="text-[12px] text-[#3973bf] mb-2 flex items-center gap-1 hover:underline">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 2l-4 4 4 4"/></svg>
                  Randevu tipine geri dön
                </button>
                <h2 className="text-[22px] xl:text-[28px] font-bold text-[#3973bf]">Kütüphane Saatleri</h2>
                <p className="text-[12px] xl:text-[14px] text-[#2b2b2b]">Kurumunuzun açtığı müsait saatlerden seçim yapın. Her rezervasyon 1 kredi düşer.</p>
              </div>
              {role === 'student' && <KalanKrediler balance={credit} />}
            </div>

            {bookingError && (
              <div className="mb-4 rounded-[10px] bg-red-50 text-red-700 px-4 py-2 text-[13px]">
                {bookingError}
              </div>
            )}

            {loadingSlots ? (
              <div className="text-center py-10 text-gray-500">Yükleniyor…</div>
            ) : slots.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-[15px] font-semibold mb-1">Henüz açılmış kütüphane saati yok</p>
                <p className="text-[13px]">Kurumunuz müsait saat eklediğinde burada gözükecek.</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                {groupedByDay.map(([day, daySlots]) => (
                  <div key={day} className="bg-white rounded-[16px] border border-blue-100 p-4">
                    <p className="text-[14px] xl:text-[16px] font-bold text-[#2b2b2b] mb-3 capitalize">{day}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
                      {daySlots.map((s) => {
                        const mine = s.my_status === 'booked';
                        const full = s.reserved_count >= s.capacity;
                        const insufficient = role === 'student' && credit !== null && credit < s.credit_cost;
                        const disabled = mine || full || insufficient || bookingId === s.id;
                        return (
                          <button
                            key={s.id}
                            onClick={() => !disabled && bookSlot(s)}
                            disabled={disabled}
                            className={`rounded-[12px] p-3 text-left border transition-colors ${
                              full
                                ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-60'
                                : insufficient
                                  ? 'bg-orange-50 border-orange-200 cursor-not-allowed'
                                  : 'bg-[#fafafa] border-[#3973bf] hover:bg-blue-50'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-[14px] font-bold text-[#2b2b2b]">
                                {fmtTime(s.start_at)} – {fmtTime(s.end_at)}
                              </span>
                              <span className="text-[10px] font-semibold text-[#3973bf] flex items-center gap-1">
                                <img src="/digipo-coin.svg" alt="" className="w-3 h-3" /> {s.credit_cost}
                              </span>
                            </div>
                            {s.assigned_teacher_name && (
                              <p className="text-[11px] text-gray-600 mb-1">Öğretmen: <span className="font-semibold text-[#2b2b2b]">{s.assigned_teacher_name}</span></p>
                            )}
                            <div className="flex items-center justify-between text-[11px]">
                              <span className={`font-semibold ${full ? 'text-red-500' : 'text-[#3973bf]'}`}>
                                {s.reserved_count}/{s.capacity} dolu
                              </span>
                              {bookingId === s.id ? (
                                <span className="text-gray-500">Rezerve ediliyor…</span>
                              ) : mine ? (
                                <span className="text-[#59a05e] font-semibold">Rezervasyonun var ✓</span>
                              ) : full ? (
                                <span className="text-red-500 font-semibold">DOLU</span>
                              ) : insufficient ? (
                                <span className="text-orange-700 font-semibold">Yetersiz kredi</span>
                              ) : (
                                <span className="text-[#3973bf] font-semibold">Rezerve Et</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 3: Onay */}
        {step === 'onay' && confirmedSlot && (
          <div className="flex flex-col items-center justify-center min-h-[350px] text-center">
            <div className="w-20 h-20 rounded-full bg-[#3973bf] flex items-center justify-center mx-auto mb-4">
              <svg width="36" height="28" viewBox="0 0 60 45" fill="none">
                <path d="M5 22L22 39L55 6" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="text-[18px] xl:text-[22px] font-bold text-[#2b2b2b] mb-2">Rezervasyon Başarılı</p>
            <div className="space-y-1 mb-5">
              <p className="text-[14px] xl:text-[16px] font-semibold text-[#3973bf]">{confirmedSlot.institution_name}</p>
              <p className="text-[13px] xl:text-[15px] text-[#2b2b2b]">
                {new Date(confirmedSlot.start_at).toLocaleDateString('tr-TR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
              </p>
              <p className="text-[14px] xl:text-[16px] font-bold text-[#2b2b2b]">
                {fmtTime(confirmedSlot.start_at)} – {fmtTime(confirmedSlot.end_at)}
              </p>
              {confirmedSlot.assigned_teacher_name && (
                <p className="text-[13px] xl:text-[15px] text-[#2b2b2b]">Öğretmen: <span className="font-semibold">{confirmedSlot.assigned_teacher_name}</span></p>
              )}
              <p className="text-[12px] xl:text-[14px] text-gray-500 mt-2">{confirmedSlot.credit_cost} kredi düşüldü</p>
            </div>
            <button
              onClick={goToRandevular}
              className="bg-[#3973bf] text-white text-[14px] xl:text-[17px] font-bold px-8 xl:px-12 py-3 rounded-[14px] hover:bg-blue-700 transition-colors"
            >
              Randevularıma Dön
            </button>
          </div>
        )}

        {/* ÖĞRETMEN STEP 1: öğretmen seçimi */}
        {step === 'ogretmen-secimi' && (
          <div>
            <button onClick={() => setStep('randevu-secimi')} className="text-[12px] text-[#3973bf] mb-2 flex items-center gap-1 hover:underline">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 2l-4 4 4 4"/></svg>
              Randevu tipine geri dön
            </button>
            <h2 className="text-[22px] xl:text-[28px] font-bold text-[#3973bf] mb-1">Öğretmen Seçin</h2>
            <p className="text-[12px] xl:text-[14px] text-[#2b2b2b] mb-5">Görüşmek istediğiniz öğretmeni seçin. Talebiniz öğretmene iletilir.</p>

            {teachersLoading ? (
              <div className="flex justify-center py-10"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
            ) : teachersError ? (
              <p className="text-red-500 text-[14px] py-6 text-center">{teachersError}</p>
            ) : teachers.length === 0 ? (
              <p className="text-gray-400 text-[14px] py-8 text-center">Size atanmış öğretmen bulunamadı.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {teachers.map((t) => (
                  <button key={t.id} onClick={() => pickTeacher(t)}
                    className="bg-[#fafafa] rounded-[16px] p-4 flex items-center gap-4 border border-blue-100 hover:shadow-md hover:bg-blue-50 transition-all text-left">
                    <div className="w-[44px] h-[44px] rounded-full bg-[#e5599b] overflow-hidden flex-shrink-0">
                      <img src="/a-avatar.png" alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-bold text-[#2b2b2b] truncate">{t.firstName} {t.lastName}</p>
                      {t.subjects.length > 0 && (
                        <p className="text-[12px] text-[#3973bf] font-semibold truncate">{t.subjects.map((s) => s.name).join(', ')}</p>
                      )}
                    </div>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#2b2b2b" strokeWidth="2"><path d="M7 4l6 6-6 6" /></svg>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ÖĞRETMEN STEP 2: tarih / saat / format / not */}
        {step === 'ogretmen-detay' && selTeacher && (
          <div>
            <button onClick={() => setStep('ogretmen-secimi')} className="text-[12px] text-[#3973bf] mb-2 flex items-center gap-1 hover:underline">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 2l-4 4 4 4"/></svg>
              Öğretmen seçimine dön
            </button>
            <h2 className="text-[22px] xl:text-[28px] font-bold text-[#3973bf] mb-1">Randevu Detayları</h2>
            <p className="text-[12px] xl:text-[14px] text-[#2b2b2b] mb-5">{selTeacher.firstName} {selTeacher.lastName}</p>

            <div className="space-y-4 max-w-[640px]">
              {selTeacher.subjects.length > 1 && (
                <div>
                  <label className="text-[14px] font-semibold text-[#2b2b2b] block mb-1.5">Ders</label>
                  <select value={subjectId ?? ''} onChange={(e) => setSubjectId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[14px] border-2 border-[#3973bf]">
                    {selTeacher.subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[13px] font-semibold text-[#2b2b2b] block mb-1.5">Tarih <span className="text-[#e5599b]">*</span></label>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                    className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px] border-2 border-[#3973bf]" />
                </div>
                <div>
                  <label className="text-[13px] font-semibold text-[#2b2b2b] block mb-1.5">Başlangıç</label>
                  <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                    className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px] border-2 border-[#3973bf]" />
                </div>
                <div>
                  <label className="text-[13px] font-semibold text-[#2b2b2b] block mb-1.5">Bitiş</label>
                  <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                    className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px] border-2 border-[#3973bf]" />
                </div>
              </div>
              {/* B7: teacher availability for the picked date */}
              {date && (
                <div className={`rounded-[12px] px-4 py-2.5 text-[13px] ${conflict ? 'bg-[#fdecef] border border-[#e5599b]' : busyError ? 'bg-[#fff8e1] border border-[#f5c518]' : 'bg-[#eef4ff] border border-[#3973bf]'}`}>
                  {busyLoading ? (
                    <span className="text-gray-500">Öğretmenin uygunluğu kontrol ediliyor…</span>
                  ) : busyError ? (
                    <span className="text-[#a97e00]">Öğretmenin uygunluğu şu anda kontrol edilemedi. Talebiniz gönderilirken saat çakışması olursa reddedilecektir.</span>
                  ) : busy.length === 0 ? (
                    <span className="text-[#2b6a2b]">Öğretmenin o gün planlı randevusu yok, seçtiğiniz saat uygun.</span>
                  ) : (
                    <>
                      <span className="font-semibold text-[#2b2b2b]">Öğretmenin dolu saatleri: </span>
                      <span className="text-[#2b2b2b]">{formatBusyRanges(busy)}</span>
                      {conflict && <p className="text-[#c53030] font-semibold mt-1">Seçtiğiniz saat dolu. Lütfen farklı bir saat seçin.</p>}
                    </>
                  )}
                </div>
              )}
              <div>
                <label className="text-[13px] font-semibold text-[#2b2b2b] block mb-1.5">Format</label>
                <div className="flex gap-2">
                  {(['face_to_face', 'online', 'phone'] as const).map((f) => (
                    <button key={f} type="button" onClick={() => setFormat(f)}
                      className="flex-1 py-2 rounded-[10px] text-[13px] font-semibold transition-colors"
                      style={{ background: format === f ? '#e5599b' : '#e5e5e5', color: format === f ? '#fff' : '#2b2b2b' }}>
                      {f === 'face_to_face' ? 'Yüz Yüze' : f === 'online' ? 'Online' : 'Telefon'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[13px] font-semibold text-[#2b2b2b] block mb-1.5">Not <span className="text-[#e5599b]">*</span></label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                  placeholder="Görüşme konusu veya ek bilgi…"
                  className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[14px] resize-none border-2 border-[#3973bf]" />
              </div>
              {createError && <p className="text-red-500 text-[13px]">{createError}</p>}
              <div className="flex gap-3 pt-1">
                <button onClick={() => setStep('ogretmen-secimi')} className="flex-1 rounded-[12px] py-3 bg-[#e5e5e5] hover:opacity-90 text-[15px] font-semibold text-[#2b2b2b]">Geri</button>
                <button onClick={submitTeacherAppointment} disabled={submitting || !date || !startTime || !endTime || !notes || conflict}
                  className="flex-1 rounded-[12px] py-3 bg-[#3973bf] hover:opacity-90 disabled:opacity-50 text-[15px] font-bold text-white">
                  {submitting ? 'Gönderiliyor…' : conflict ? 'Saat Dolu' : 'Randevu Talep Et'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ÖĞRETMEN STEP 3: onay */}
        {step === 'ogretmen-onay' && (
          <div className="flex flex-col items-center justify-center min-h-[350px] text-center">
            <div className="w-20 h-20 rounded-full bg-[#3973bf] flex items-center justify-center mx-auto mb-4">
              <svg width="36" height="28" viewBox="0 0 60 45" fill="none">
                <path d="M5 22L22 39L55 6" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="text-[18px] xl:text-[22px] font-bold text-[#2b2b2b] mb-2">Randevu Talebiniz Gönderildi</p>
            <p className="text-[13px] xl:text-[15px] text-gray-500 mb-5 max-w-[420px]">
              Öğretmen talebinizi onayladıktan sonra bildirim alacaksınız.
            </p>
            <button
              onClick={goToRandevular}
              className="bg-[#3973bf] text-white text-[14px] xl:text-[17px] font-bold px-8 xl:px-12 py-3 rounded-[14px] hover:bg-blue-700 transition-colors"
            >
              Randevularıma Dön
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RandevuOlusturModal;
